import {
	checkNearestValidAmount,
	type CURRENCYCODETYPE,
	formatNumberWithCommas,
	getCleanedValue,
	getNearestValidAmount,
	handleDecimalPoints,
	type IWalletProps,
} from 'BreetHelpers';
import { type ChangeEvent, useCallback, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import type { CreateWithdrawRequestFormSchemaType } from '@/components/DialogWithdrawRequest/DialogWithdrawRequest.helpers';

interface UseFiatWithdrawAmountArgs {
	hookForm: UseFormReturn<CreateWithdrawRequestFormSchemaType>;
	userWalletProps: IWalletProps;
	withdrawalMinAmount: number;
	withdrawalMaxAmount: number;
	currencyCode: CURRENCYCODETYPE;
}

export const useFiatWithdrawAmount = ({
	hookForm,
	userWalletProps,
	withdrawalMinAmount,
	withdrawalMaxAmount,
	currencyCode,
}: UseFiatWithdrawAmountArgs) => {
	const [inputMessage, setInputMessage] = useState('');

	const {
		setError,
		clearErrors,
		setValue,
		formState: { errors },
	} = hookForm;

	const validateAmount = useCallback(
		(cleanedValue: string) => {
			const numericAmount = Number(cleanedValue.replace(/,/g, ''));

			if (numericAmount > userWalletProps.walletBalance) {
				setError('amount', { message: 'Insufficient balance' });
				return;
			}

			if (numericAmount < withdrawalMinAmount) {
				setError('amount', {
					message: `The minimum withdrawal amount is ${userWalletProps.symbol} ${formatNumberWithCommas(withdrawalMinAmount)}`,
				});
				return;
			}

			const effectiveMaxAmount = withdrawalMaxAmount > 0 ? withdrawalMaxAmount : userWalletProps.walletBalance;

			if (numericAmount > effectiveMaxAmount) {
				setError('amount', {
					message: `The maximum withdrawal amount is ${userWalletProps.symbol} ${formatNumberWithCommas(effectiveMaxAmount)}`,
				});
				return;
			}

			const nearestValidAmountError = checkNearestValidAmount(numericAmount, currencyCode);

			if (nearestValidAmountError) {
				setError('amount', { message: nearestValidAmountError });
				return;
			}

			clearErrors('amount');
		},
		[
			userWalletProps.walletBalance,
			userWalletProps.symbol,
			withdrawalMinAmount,
			withdrawalMaxAmount,
			currencyCode,
			clearErrors,
			setError,
		]
	);

	const commitAmount = useCallback(
		(rawValue: string) => {
			let cleanedValue = getCleanedValue(rawValue);
			cleanedValue = handleDecimalPoints(cleanedValue);

			setValue('amount', cleanedValue, { shouldDirty: true });
			validateAmount(cleanedValue);
		},
		[setValue, validateAmount]
	);

	const handleAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			if (!!errors.amount?.message || !value) clearErrors('amount');

			commitAmount(value);

			if (inputMessage) setInputMessage('');
		},
		[errors.amount?.message, clearErrors, commitAmount, inputMessage]
	);

	const handleMaxClick = useCallback(() => {
		const effectiveMaxAmount = withdrawalMaxAmount > 0 ? withdrawalMaxAmount : userWalletProps.walletBalance;
		const amount = Math.min(userWalletProps.walletBalance, effectiveMaxAmount);

		setInputMessage(
			userWalletProps.walletBalance > effectiveMaxAmount
				? `Maximum withdrawal per transaction is ${userWalletProps.symbol} ${formatNumberWithCommas(effectiveMaxAmount)}`
				: ''
		);

		commitAmount(getNearestValidAmount(amount, currencyCode === 'NGN').toString());
	}, [userWalletProps.walletBalance, userWalletProps.symbol, withdrawalMaxAmount, currencyCode, commitAmount]);

	return { inputMessage, handleAmountChange, handleMaxClick };
};
